import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { BUILD_VERSION } from "@/lib/buildTag";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function HomePortal() {
  const { user, login, logout, userGestioni, hasGestione, loading } = useAuth();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);

  // Auto-redirect: only one gestione -> enter directly (unless user just exited from a gestione)
  useEffect(() => {
    if (!user) return;
    // Alunni: li mandiamo sempre al loro portale
    if (user.user_type === "alunno") {
      navigate("/alunni", { replace: true });
      return;
    }
    let stayHere = false;
    try {
      if (sessionStorage.getItem("stay_on_home") === "1") {
        stayHere = true;
        sessionStorage.removeItem("stay_on_home");
      }
    } catch { /* ignore */ }
    if (stayHere) return;
    const g = userGestioni();
    if (g.length === 1) {
      navigate(`/g/${g[0]}`, { replace: true });
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mentre redirige un alunno, evita di mostrare la home admin (anche per un
  // solo frame): mostra uno schermo neutro così non vede la lista gestioni.
  if (user && user.user_type === "alunno") {
    return (
      <div data-testid="home-portal-alunno-redirect"
           style={{ minHeight: "100vh", background: "linear-gradient(135deg,#122A4C 0%,#0A2B52 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 14 }}>
        Reindirizzamento al portale alunni…
      </div>
    );
  }

  const go = (path) => { if (user) navigate(path); };
  const onLoginSuccess = () => setShowLogin(false);

  // 🆕 Giu 2026: HOME riorganizzata in card raggruppate a elenco (schema PDF utente).
  // Ogni voce è filtrata per permesso (hasGestione) e la card appare solo se ha ≥1 voce.
  const CARDS = [
    {
      title: "AZIENDA", color: "#1565c0", icon: "🏢",
      voices: [
        { label: "Azienda", path: "/g/azienda/azienda_anagrafica", show: () => hasGestione("azienda") },
        { label: "Setup e Sync", path: "/g/admin/impostazioni", show: () => hasGestione("admin") },
        { label: "Utenti", path: "/g/admin/utenti", show: () => hasGestione("admin") },
        { label: "Log Portale Alunni", path: "/g/portale_log/alunni_portal_log", show: () => hasGestione("portale_log") },
      ],
    },
    {
      title: "ELENCHI E TABELLE", color: "#00838f", icon: "🗂",
      voices: [
        { label: "SegreMat Web Pro", path: "/g/segremat/alunni", show: () => hasGestione("segremat") },
        { label: "Movimenti", path: "/g/segremat/movimenti", show: () => hasGestione("segremat") },
        { label: "Tabelle Fisse", path: "/g/tabelle_alunni", show: () => hasGestione("tabelle_alunni") },
        { label: "Tabelle Personalizzate", path: hasGestione("tabelle_alunni") ? "/g/tabelle_alunni/tabelle_personalizzate" : "/g/tabelle_personalizzate/tabelle_personalizzate", show: () => hasGestione("tabelle_alunni") || hasGestione("tabelle_personalizzate") },
      ],
    },
    {
      title: "ISCRIZIONI", color: "#6a1b9a", icon: "📝",
      voices: [
        { label: "Alunni vs Classi Liberi", path: "/g/segremat/scuola_curriculum", show: () => hasGestione("segremat") },
        { label: "Agevolazioni", path: "/g/agevolazioni", show: () => hasGestione("agevolazioni") },
        { label: "Extra", path: "/g/extra", show: () => hasGestione("extra") },
        { label: "Spot", path: "/g/richieste_famiglie/rf_richieste", show: () => hasGestione("richieste_famiglie") },
        { label: "Iscrizioni Open Day", path: "/g/openday/openday_iscrizioni", show: () => hasGestione("openday") },
      ],
    },
    {
      title: "MENSA", color: "#c2185b", icon: "🍽️",
      voices: [
        { label: "Elabora Mensa Giorno", path: "/g/mensa/mensa_giorno", show: () => hasGestione("mensa") },
        { label: "Mensa Giorno", path: "/g/mensa/mensa_giorno", show: () => hasGestione("mensa") },
        { label: "Mensa Mese", path: "/g/mensa_mese/mensa_mese_periodi", show: () => hasGestione("mensa_mese") },
        { label: "Mensa Matrice", path: "/g/mensa/mensa_matrice", show: () => hasGestione("mensa") },
      ],
    },
    {
      title: "CEDOLE LIBRI", color: "#6d4c41", icon: "📚",
      voices: [
        { label: "Prenotazione Libri", path: "/g/libri_prenotazione/prenotazioni_libri", show: () => hasGestione("libri_prenotazione") },
        { label: "Cedole Libro Comuni", path: "/g/cedole_prenotazione/cedole", show: () => hasGestione("cedole_prenotazione") },
        { label: "Settori Classi Libreria", path: "/g/segremat/libreria", show: () => hasGestione("segremat") },
      ],
    },
    {
      title: "CONTABILITÀ", color: "#2e7d32", icon: "📒",
      voices: [
        { label: "Prima Nota", path: "/g/prima_nota/prima_nota_fatture", show: () => hasGestione("prima_nota") },
        { label: "Divisione Ricavi", path: "/g/bilancio/dashboard", show: () => hasGestione("bilancio") },
      ],
    },
    {
      title: "CASSE E BANCA", color: "#c62828", icon: "🏦",
      voices: [
        { label: "Pagamenti Multipli", path: "/g/riconciliazione/riconciliazione_incassi", show: () => hasGestione("riconciliazione") },
        { label: "Stati di Cassa", path: "/g/stati_cassa/stati_cassa", show: () => hasGestione("stati_cassa") },
        { label: "Contabilità Canossa", path: "/g/cassa/elaborazione_periodo", show: () => hasGestione("cassa") },
        { label: "Estratto Conto", path: "/g/banca/banca", show: () => hasGestione("banca") },
        { label: "Scadenzario", path: "/g/controlli/report_addebiti", show: () => hasGestione("controlli") },
      ],
    },
    {
      title: "CREDITO", color: "#0097a7", icon: "💳",
      voices: [
        { label: "Situazioni Contabili", path: "/g/credito/comunicator", show: () => hasGestione("credito") },
        { label: "Situazioni Contabili Visual", path: "/g/credito/credito_situazioni", show: () => hasGestione("credito") },
      ],
    },
    {
      title: "COMUNICAZIONE", color: "#00acc1", icon: "📧",
      voices: [
        { label: "Mail e Dichiarazioni", path: "/g/comunicator/comunicator", show: () => hasGestione("comunicator") },
      ],
    },
    {
      title: "DOCENTI", color: "#0277bd", icon: "👨‍🏫",
      voices: [
        { label: "Docenti", path: "/g/docenti/docenti", show: () => hasGestione("docenti") },
        { label: "Mensa Docenti", path: hasGestione("mense_docenti") ? "/mensa-docenti" : "/portineria/presenze", show: () => hasGestione("portineria") || hasGestione("mensa") || hasGestione("mense_docenti") },
      ],
    },
    {
      title: "STATISTICHE", color: "#283593", icon: "📊",
      voices: [
        { label: "Analytics", path: "/g/analytics/analytics_dashboard", show: () => hasGestione("analytics") },
      ],
    },
    {
      title: "ALTRO", color: "#546e7a", icon: "🧰",
      voices: [
        { label: "Acquisti", path: "/g/acquisti/acquisti_fornitori", show: () => hasGestione("acquisti") },
        { label: "Privacy", path: "/g/privacy/privacy_trattamenti", show: () => hasGestione("privacy") },
        { label: "FAQ", path: "/g/faq/faq", show: () => hasGestione("faq") },
        { label: "Stampe", path: "/g/stampe/stampe", show: () => hasGestione("stampe") },
      ],
    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #122A4C 0%, #0A2B52 100%)", padding: 24 }} data-testid="home-portal">
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 16px" }}>
        {/* Header with logo + auth button */}
        <div style={{ marginBottom: 22, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <img
            src="https://customer-assets.emergentagent.com/job_data-extraction-tool-8/artifacts/127arsv9_ccea6996-b7a7-4ac9-add3-72bd2e7d3685.png"
            alt="Istituto Canossiano"
            data-testid="logo-canossa"
            style={{ height: 75, width: "auto", borderRadius: 4 }}
          />
          <div>
            <div style={{ color: "white", fontStyle: "italic", fontSize: 28, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1 }}>
              canossa<span style={{ color: "#ffdd00" }}>HUB</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 13, marginTop: 4 }}>
              Portale Gestioni · Seleziona l&apos;area in cui lavorare
            </div>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
            {user && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="home-anno-selector">
                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>A.S.</span>
                <AnnoAttivoSelector size="large" />
              </div>
            )}
            {user && (
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, textAlign: "right" }}>
                <div style={{ fontSize: 10, opacity: 0.7 }}>Utente</div>
                <div style={{ fontWeight: 700 }} data-testid="header-username">{user.username}</div>
              </div>
            )}
            {!user ? (
              <a
                data-testid="btn-open-login"
                href={`${window.location.origin}/api/auth/google/login`}
                style={{
                  padding: "8px 18px", background: "#ffdd00", color: "#0A2B52",
                  border: "none", fontSize: 12, fontWeight: 800,
                  cursor: loading ? "wait" : "pointer", borderRadius: 3,
                  letterSpacing: 0.5, boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <g>
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.257h2.908c1.702-1.567 2.684-3.874 2.684-6.614z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.257c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                    <path d="M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.712V4.956H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.044l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.956L3.964 7.288C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </g>
                </svg>
                ACCEDI CON GOOGLE
              </a>
            ) : (
              <button
                data-testid="btn-logout" onClick={logout}
                style={{
                  padding: "8px 18px", background: "rgba(255,255,255,0.12)", color: "white",
                  border: "1px solid rgba(255,255,255,0.3)", fontSize: 12, fontWeight: 700,
                  cursor: "pointer", borderRadius: 3, letterSpacing: 0.5,
                }}
              >ESCI</button>
            )}
          </div>
        </div>

        {/* Cards raggruppate - visibili solo dopo login, filtrate per permessi utente */}
        {!user ? (
          <div data-testid="portal-locked" style={{ padding: 40, background: "rgba(255,255,255,0.06)", border: "1px dashed rgba(255,255,255,0.25)", borderRadius: 8, textAlign: "center", color: "rgba(255,255,255,0.75)", fontSize: 14 }}>
            Accedi per visualizzare le gestioni disponibili.
          </div>
        ) : (
          (() => {
            const cards = CARDS
              .map(c => ({ ...c, vis: c.voices.filter(v => v.show()) }))
              .filter(c => c.vis.length > 0);
            if (cards.length === 0) {
              return (
                <div data-testid="portal-no-gestioni" style={{ padding: 40, background: "rgba(255,200,0,0.14)", border: "1px solid rgba(255,200,0,0.4)", borderRadius: 8, color: "#ffe082", fontSize: 14, textAlign: "center" }}>
                  Non hai gestioni abilitate. Contatta l&apos;amministratore.
                </div>
              );
            }
            return (
              <div data-testid="home-cards-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(275px, 1fr))",
                  gridAutoRows: "1fr",
                  gap: 16,
                }}>
                {cards.map(card => (
                  <div key={card.title} data-testid={`home-card-${slug(card.title)}`}
                    style={{ background: "white", borderRadius: 8, overflow: "hidden", boxShadow: "0 6px 20px rgba(0,0,0,0.30)", display: "flex", flexDirection: "column", height: "100%" }}>
                    <div style={{ background: card.color, color: "white", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: 0.5 }}>{card.title}</span>
                      <span style={{ fontSize: 20, lineHeight: 1, opacity: 0.92 }}>{card.icon}</span>
                    </div>
                    <div style={{ padding: "8px 6px", flex: 1 }}>
                      {card.vis.map(v => (
                        <button
                          key={v.label}
                          data-testid={`home-voice-${slug(v.label)}`}
                          onClick={() => go(v.path)}
                          style={{
                            display: "flex", alignItems: "center", gap: 8, width: "100%",
                            textAlign: "left", background: "none", border: "none", cursor: "pointer",
                            padding: "5px 12px", fontSize: 15, color: "#2a3b4d", borderRadius: 5,
                            transition: "background .12s ease, color .12s ease, transform .12s ease",
                          }}
                          onMouseOver={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = card.color; e.currentTarget.style.transform = "translateX(3px)"; }}
                          onMouseOut={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#2a3b4d"; e.currentTarget.style.transform = "none"; }}
                        >
                          <span style={{ color: card.color, fontSize: 12, flexShrink: 0 }}>▸</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{v.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        )}

        {/* Institutional info footer */}
        <div style={{ marginTop: 20, padding: "12px 16px", background: "rgba(0,0,0,0.28)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 4, color: "rgba(255,255,255,0.88)", fontSize: 11, lineHeight: 1.5 }} data-testid="institutional-info">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0 10px", rowGap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: "#ffdd00" }}>ISTITUTO CANOSSIANO</span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>·</span>
              <span>Via A. Diaz, 30 · 25124 Brescia</span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>·</span>
              <span>Tel. <a href="tel:+390302886511" style={{ color: "#fff", textDecoration: "none", fontWeight: 600 }}>030.2886511</a></span>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>·</span>
              <a href="mailto:segreteria@canossadiaz.it" style={{ color: "#fff" }}>segreteria@canossadiaz.it</a>
              <span style={{ color: "rgba(255,255,255,0.35)" }}>|</span>
              <a href="mailto:segrescuole@canossadiaz.it" style={{ color: "#fff" }}>segrescuole@canossadiaz.it</a>
            </div>
            <div data-testid="home-build-tag"
                 title="Versione attuale dell'applicazione (utile per verificare la propagazione di un Deploy)"
                 style={{ fontFamily: "Consolas, monospace", fontSize: 12,
                          padding: "5px 12px",
                          background: "rgba(0,0,0,0.5)",
                          border: "1px solid rgba(255,255,255,0.25)",
                          color: "rgba(255,255,255,0.95)",
                          letterSpacing: 0.4, fontWeight: 700,
                          alignSelf: "flex-start", borderRadius: 2 }}>
              v. {BUILD_VERSION}
            </div>
          </div>
        </div>
      </div>

      {showLogin && !user && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSuccess={onLoginSuccess}
          login={login}
        />
      )}
    </div>
  );
}

function LoginModal({ onClose, onSuccess, login }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await login(username, password);
      onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Credenziali non valide");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-modal"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: 400, borderRadius: 6, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
        <div style={{ background: "linear-gradient(135deg, #0066cc, #0099ff)", padding: "16px 20px", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>⚙ Accesso Amministratore</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>Solo per account tecnico</div>
          </div>
          <button
            data-testid="login-close"
            onClick={onClose}
            style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer", fontWeight: 700 }}
          >✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 22 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Nome utente</label>
            <input
              data-testid="login-username"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoFocus
              style={{ width: "100%", height: 38, border: "1px solid #bbb", padding: "0 10px", fontSize: 14, borderRadius: 2, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4 }}>Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ width: "100%", height: 38, border: "1px solid #bbb", padding: "0 10px", fontSize: 14, borderRadius: 2, boxSizing: "border-box" }}
            />
          </div>
          {error && (
            <div data-testid="login-error" style={{ background: "#fff0f0", border: "1px solid #fcc", padding: "6px 10px", marginBottom: 12, color: "#cc0000", fontSize: 11, fontWeight: 600 }}>
              {error}
            </div>
          )}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            style={{ width: "100%", height: 40, background: "#0066cc", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: submitting ? "wait" : "pointer", borderRadius: 2, letterSpacing: 0.5 }}
          >
            {submitting ? "Accesso in corso..." : "ACCEDI"}
          </button>
          <div style={{ fontSize: 10, color: "#888", marginTop: 14, textAlign: "center" }}>
            Gli utenti <b>@canossadiaz.it</b> devono usare il bottone <b>Accedi con Google</b> in alto a destra.
          </div>
        </form>
      </div>
    </div>
  );
}
